/**
 * Trustpilot TrustBox under PDP Add to Cart, above BNPL.
 *
 * Config from BM Site Preferences (group Trustpilot), exposed via MRT
 * `site.preferences.trustpilot` (getTrustpilot in preferenceHelpers).
 *
 * Micro Combo at the default 187px slot = Excellent + 5 stars only. Wider
 * and it also paints "N reviews on Trustpilot" (duplicate of our lockup).
 * The wordmark wraps when the buy-box cannot fit slot + lockup on one row
 * (iPad Mini landscape, iPad Pro portrait, any narrow column).
 *
 * @see https://help.trustpilot.com/s/article/Add-a-TrustBox-widget-to-a-single-page-application
 */
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef} from 'react';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {Box, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useTrustpilotScript} from '@amblique/king-living/app/hooks/use-trustpilot-script';

/**
 * Presentation defaults (not BM) — Micro Combo Excellent + 5 stars.
 * Keep the iframe at this width so the review-count + TP logo stay clipped.
 */
const WIDGET_STYLE = {
  styleHeight: '20px',
  styleWidth: '187px',
  theme: 'light',
};

/** Debounce Trustpilot reload on container resize (orientation / layout shift). */
const RESIZE_RELOAD_MS = 120;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isPrefEnabled(value) {
  return value === true || value === 'true';
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function asPrefString(value) {
  if (value == null || value === '') {
    return '';
  }

  if (typeof value === 'object' && value.value != null) {
    return String(value.value);
  }

  return String(value);
}

/** Figma lockup: green Trustpilot star (14×14) beside wordmark. */
const TrustpilotWordmark = () => (
  <Flex align="center" aria-hidden="true" flexShrink={0} gap="4px">
    <Box as="span" display="inline-flex" h="14px" w="14px">
      <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 10.1042L10.0417 9.33333L11.3125 13.25L7 10.1042ZM14 5.04167H8.64583L7 0L5.35417 5.04167H0L4.33334 8.16667L2.6875 13.2083L7.02083 10.0833L9.6875 8.16667L14 5.04167Z" fill="#219653"/>
      </svg>
    </Box>
    <Text
      as="span"
      color="black"
      fontFamily="Roboto, Helvetica, Arial, sans-serif"
      fontSize="12px"
      fontWeight="400"
      lineHeight="26px"
    >
      Trustpilot
    </Text>
  </Flex>
);

export function useTrustpilotMicroConfig({
  businessUnitId: businessUnitIdProp,
  locale: localeProp,
  reviewUrl: reviewUrlProp,
  templateId: templateIdProp,
} = {}) {
  const trustpilotPrefs = useSitePreference('trustpilot');

  return useMemo(() => {
    const enabled = isPrefEnabled(trustpilotPrefs?.enabled);
    const businessUnitId =
      businessUnitIdProp || asPrefString(trustpilotPrefs?.businessUnitId);
    const templateId = templateIdProp || asPrefString(trustpilotPrefs?.templateId);
    const locale = localeProp || asPrefString(trustpilotPrefs?.locale);
    const reviewUrl = reviewUrlProp || '';

    return {
      enabled: enabled && Boolean(businessUnitId && templateId),
      businessUnitId,
      locale,
      reviewUrl,
      templateId,
    };
  }, [trustpilotPrefs, businessUnitIdProp, localeProp, reviewUrlProp, templateIdProp]);
}

const TrustpilotMicro = ({
  businessUnitId: businessUnitIdProp,
  templateId: templateIdProp,
  locale: localeProp,
  reviewUrl: reviewUrlProp,
  styleHeight = WIDGET_STYLE.styleHeight,
  styleWidth = WIDGET_STYLE.styleWidth,
  theme = WIDGET_STYLE.theme,
  ...rest
}) => {
  const config = useTrustpilotMicroConfig({
    businessUnitId: businessUnitIdProp,
    locale: localeProp,
    reviewUrl: reviewUrlProp,
    templateId: templateIdProp,
  });

  const ref = useRef(null);
  const widgetSlotRef = useRef(null);
  const isScriptReady = useTrustpilotScript(config.enabled);

  useEffect(() => {
    if (!config.enabled || !isScriptReady || !ref.current) {
      return undefined;
    }

    const widgetEl = ref.current;

    const loadWidget = () => {
      try {
        if (!window.Trustpilot?.loadFromElement) {
          console.warn('[Trustpilot] window.Trustpilot missing after bootstrap — check CSP script-src');
          return;
        }
        window.Trustpilot.loadFromElement(widgetEl, true);
      } catch (error) {
        console.warn('[Trustpilot] loadFromElement failed', error);
      }
    };

    loadWidget();

    // Re-load when buy-box width changes (iPad rotate / responsive column) so Micro
    // Combo re-evaluates whether to show the full star row.
    const slotEl = widgetSlotRef.current;
    if (typeof ResizeObserver === 'undefined' || !slotEl) {
      return undefined;
    }

    let timerId = null;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timerId);
      timerId = window.setTimeout(loadWidget, RESIZE_RELOAD_MS);
    });

    observer.observe(slotEl);

    return () => {
      window.clearTimeout(timerId);
      observer.disconnect();
    };
  }, [
    isScriptReady,
    config.enabled,
    config.businessUnitId,
    config.templateId,
    config.locale,
    styleHeight,
    styleWidth,
  ]);

  if (!config.enabled) {
    return null;
  }

  return (
    <Flex
      className="trustpilot-micro"
      data-testid="trustpilot-micro"
      align="center"
      justify="center"
      flexDirection="row"
      flexWrap="wrap"
      borderRadius="0"
      columnGap="13px"
      rowGap="4px"
      minH="40px"
      maxW="100%"
      minW={0}
      overflow="visible"
      px={4}
      py={1.5}
      w="100%"
      {...rest}
    >
      <Box
        ref={widgetSlotRef}
        className="trustpilot-micro__widget"
        flex={`0 0 ${styleWidth}`}
        minW={styleWidth}
        overflow="hidden"
        w={styleWidth}
        sx={{
          '& .trustpilot-widget': {
            minWidth: styleWidth,
            overflow: 'hidden',
            width: styleWidth,
          },
          '& iframe': {
            maxWidth: styleWidth,
            width: styleWidth,
          },
        }}
      >
        <div
          ref={ref}
          className="trustpilot-widget"
          data-locale={config.locale}
          data-template-id={config.templateId}
          data-businessunit-id={config.businessUnitId}
          data-style-height={styleHeight}
          data-style-width={styleWidth}
          data-theme={theme}
        >
          {config.reviewUrl ? (
            <a href={config.reviewUrl} target="_blank" rel="noopener noreferrer">
              Trustpilot
            </a>
          ) : (
            <span>Trustpilot</span>
          )}
        </div>
      </Box>

      {config.reviewUrl ? (
        <Flex
          as="a"
          href={config.reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          align="center"
          flexShrink={0}
          aria-label="Trustpilot"
        >
          <TrustpilotWordmark />
        </Flex>
      ) : (
        <Flex align="center" flexShrink={0} aria-hidden="true">
          <TrustpilotWordmark />
        </Flex>
      )}
    </Flex>
  );
};

TrustpilotMicro.displayName = 'TrustpilotMicro';

TrustpilotMicro.propTypes = {
  businessUnitId: PropTypes.string,
  templateId: PropTypes.string,
  locale: PropTypes.string,
  reviewUrl: PropTypes.string,
  styleHeight: PropTypes.string,
  styleWidth: PropTypes.string,
  theme: PropTypes.string,
};

export default TrustpilotMicro;
